import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, FileText, Upload, MessageSquare, ClipboardCheck, ReceiptText,
  Milestone, CheckCircle2, Send, RefreshCw, Ban, ShieldAlert
} from "lucide-react";
import DashboardShell from "@/layouts/ProviderDashboardLayout";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useDocumentTitle } from "@/lib/useDocumentTitle";
import StatusPill from "@/components/marketplace/StatusPill";
import StripeCheckoutPanel from "@/components/marketplace/StripeCheckoutPanel";
import TipButton from "@/components/marketplace/TipButton";
import MessageThread from "@/components/marketplace/MessageThread";
import ReviewForm from "@/components/marketplace/ReviewForm";
import OpenDisputeDialog from "@/components/marketplace/OpenDisputeDialog";

const TABS = [
  { key: "overview", label: "Overview", icon: ClipboardCheck },
  { key: "requirements", label: "Requirements", icon: FileText },
  { key: "deliveries", label: "Deliveries", icon: Upload },
  { key: "milestones", label: "Milestones", icon: Milestone },
  { key: "activity", label: "Activity log", icon: ReceiptText },
  { key: "messages", label: "Messages", icon: MessageSquare },
];

export default function OrderWorkspacePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("overview");
  const [myReview, setMyReview] = useState(null);
  const [disputeOpen, setDisputeOpen] = useState(false);
  useDocumentTitle(data?.order?.service_title ? `Order · ${data.order.service_title}` : "Order");

  const load = async () => {
    try {
      const { data } = await api.get(`/digital-orders/${id}`);
      setData(data);
    } catch (err) {
      toast.error("Could not load order");
      navigate("/dashboard/orders");
    }
  };
  useEffect(() => { load(); }, [id]);

  useEffect(() => {
    if (!data?.order?.id) return;
    (async () => {
      try {
        const { data: mine } = await api.get("/reviews/mine");
        const found = (mine || []).find((r) => (r.order_id === data.order.id || r.target_id === data.order.id) && r.reviewer_id === user?.id);
        setMyReview(found || null);
      } catch (_) { /* ignore */ }
    })();
  }, [data?.order?.id, user?.id]);

  if (!data) return <DashboardShell><div className="p-6 text-[#252A34]/60">Loading order…</div></DashboardShell>;

  const o = data.order;
  const isProvider = o.provider_user_id === user?.id;
  const isCustomer = o.customer_id === user?.id;

  const completeOrder = async () => {
    try {
      await api.post(`/digital-orders/${o.id}/complete`);
      toast.success("Order marked complete");
      load();
    } catch (err) { toast.error(err?.response?.data?.error || "Cannot complete"); }
  };

  const startWork = async () => {
    try {
      await api.post(`/digital-orders/${o.id}/start-work`);
      toast.success("Work started — good luck!");
      load();
    } catch (err) { toast.error(err?.response?.data?.error || "Cannot start work"); }
  };

  const filteredTabs = TABS.filter((t) => t.key !== "milestones" || o.is_milestone_project);

  return (
    <DashboardShell>
      <div className="max-w-6xl">
        <button onClick={() => navigate(-1)} className="text-sm text-[#165a71] font-medium inline-flex items-center gap-2 mb-4">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
          <div>
            <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#165a71]">Digital order</div>
            <h1 className="mt-2 font-display text-2xl md:text-3xl font-semibold text-[#071b22]" data-testid="order-title">{o.service_title}</h1>
            <div className="mt-2 text-sm text-[#252A34]/70">
              {o.due_date && <>Due: {new Date(o.due_date).toLocaleDateString()}</>}
              {o.revision_allowance !== undefined && <> · Revisions: {o.revisions_used || 0}/{o.revision_allowance}</>}
            </div>
          </div>
          <StatusPill status={o.status} size="lg" />
        </div>

        {o.status === "payment_pending" && isCustomer && (
          <div className="mb-6" data-testid="pay-panel">
            <StripeCheckoutPanel purpose="order" targetId={o.id} amount={o.total_estimate} ctaLabel="Pay with card" />
          </div>
        )}
        {o.status === "completed" && isCustomer && (
          <div className="mb-6 flex justify-end gap-2 flex-wrap">
            {!o.is_disputed && <button onClick={() => setDisputeOpen(true)} className="inline-flex items-center gap-2 rounded-full border border-rose-200 bg-white text-rose-700 px-4 py-2 text-sm font-medium hover:bg-rose-50" data-testid="order-open-dispute-btn"><ShieldAlert className="h-4 w-4" /> Open dispute</button>}
            <TipButton targetId={o.id} providerName={data.provider?.business_name || data.provider?.display_name} testId="order-tip-btn" />
          </div>
        )}
        {o.status === "completed" && isProvider && !o.is_disputed && (
          <div className="mb-6 flex justify-end">
            <button onClick={() => setDisputeOpen(true)} className="inline-flex items-center gap-2 rounded-full border border-rose-200 bg-white text-rose-700 px-4 py-2 text-sm font-medium hover:bg-rose-50" data-testid="order-open-dispute-btn"><ShieldAlert className="h-4 w-4" /> Open dispute</button>
          </div>
        )}
        {o.is_disputed && (
          <div className="mb-6 rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800" data-testid="order-dispute-banner">
            This order is currently <b>in dispute</b>. Provider payout is held in the reserved balance until it's resolved.
            {o.dispute_id && <> <Link className="underline font-medium ml-1" to={`/dashboard/disputes/${o.dispute_id}`}>Open case →</Link></>}
          </div>
        )}

        <div className="border-b border-slate-200 mb-6 flex gap-6 overflow-x-auto">
          {filteredTabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`inline-flex items-center gap-2 pb-3 text-sm font-medium border-b-2 ${tab === t.key ? "border-[#165a71] text-[#071b22]" : "border-transparent text-[#252A34]/60 hover:text-[#252A34]"}`}
              data-testid={`order-tab-${t.key}`}
            >
              <t.icon className="h-4 w-4" /> {t.label}
            </button>
          ))}
        </div>

        {tab === "overview" && (
          <div className="grid gap-6 lg:grid-cols-3">
            <div className="lg:col-span-2 space-y-4">
              <div className="rounded-2xl border border-slate-200 bg-white p-5">
                <div className="font-display font-semibold text-[#071b22] mb-2">Service</div>
                <div className="text-sm text-[#252A34]">{data.service?.short_description || data.order?.service_title || "—"}</div>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-5">
                <div className="font-display font-semibold text-[#071b22] mb-2">Actions</div>
                <div className="flex flex-wrap gap-2">
                  {isCustomer && o.status === "delivered" && (
                    <button className="btn-primary" onClick={completeOrder} data-testid="order-complete-btn">
                      <CheckCircle2 className="h-4 w-4 mr-1" /> Accept & complete
                    </button>
                  )}
                  {isProvider && o.status === "requirements_needed" && (data.requirements || []).length === 0 && (
                    <button className="btn-primary" onClick={startWork} data-testid="order-start-work-btn">
                      <Send className="h-4 w-4 mr-1" /> Start work
                    </button>
                  )}
                </div>
                {isProvider && o.status === "requirements_needed" && (data.requirements || []).length === 0 && (
                  <div className="mt-3 rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
                    This service defines no requirements — click <b>Start work</b> to begin. (Payment already received in test mode.)
                  </div>
                )}
              </div>
            </div>
            <div className="space-y-4">
              <div className="rounded-2xl border border-slate-200 bg-white p-5">
                <div className="font-display font-semibold text-[#071b22] mb-3">Pricing</div>
                <Row label="Subtotal" value={`$${(o.subtotal || 0).toFixed(2)}`} />
                {o.addons_total > 0 && <Row label="Add-ons" value={`+$${o.addons_total.toFixed(2)}`} />}
                <div className="mt-3 pt-3 border-t border-slate-200 flex items-center justify-between">
                  <div className="text-sm text-[#252A34]/70">Total</div>
                  <div className="font-display text-xl font-semibold text-[#071b22]">${(o.total_estimate || 0).toFixed(2)}</div>
                  {o.deal_discount > 0 && <div className="text-xs text-[#8a3b1e] mt-0.5" data-testid="order-deal-line">Includes deal "{o.deal_title}" (−${Number(o.deal_discount).toFixed(2)})</div>}
                </div>
                <div className="mt-2 text-[11px] text-[#252A34]/60">Payment: {o.payment_status}{o.payment_simulated ? " (DEV simulated)" : ""}</div>
              </div>
            </div>
          </div>
        )}

        {tab === "requirements" && (
          <RequirementsPanel o={o} requirements={data.requirements || []} isCustomer={isCustomer} onChange={load} />
        )}
        {tab === "deliveries" && (
          <DeliveriesPanel o={o} isProvider={isProvider} isCustomer={isCustomer} onChange={load} />
        )}
        {tab === "milestones" && o.is_milestone_project && (
          <MilestonesPanel o={o} isProvider={isProvider} isCustomer={isCustomer} onChange={load} />
        )}
        {tab === "activity" && (
          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <div className="font-display font-semibold text-[#071b22] mb-3">Activity log</div>
            <div className="space-y-3">
              {(o.status_history || []).map((h, i) => (
                <div key={i} className="flex items-start gap-3 text-sm" data-testid={`order-activity-${i}`}>
                  <div className="mt-1 h-2 w-2 rounded-full bg-[#165a71]" />
                  <div>
                    <div className="font-medium capitalize">{h.from?.replace(/_/g, " ")} → {h.to?.replace(/_/g, " ")}</div>
                    <div className="text-xs text-[#252A34]/60">{h.at ? new Date(h.at).toLocaleString() : ""}</div>
                    {h.meta?.dev_simulated_payment && <div className="text-[11px] uppercase tracking-wider text-amber-700 font-medium">DEV simulated payment</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {tab === "messages" && (
          <div className="max-w-3xl">
            <MessageThread conversationId={o.conversation_id} otherPartyName={isProvider ? "Customer" : (data.provider?.business_name || data.provider?.display_name)} />
          </div>
        )}

        {o.status === "completed" && !myReview && (isCustomer || isProvider) && (
          <div className="mt-8" data-testid="order-review-slot">
            <ReviewForm
              transaction={{ ...o, target_collection: "orders" }}
              direction={isCustomer ? "customer_to_provider" : "provider_to_customer"}
              onSubmitted={() => { setMyReview({ pending: true }); load(); }}
            />
          </div>
        )}
      </div>
      {disputeOpen && (
        <OpenDisputeDialog
          open={disputeOpen}
          onClose={() => setDisputeOpen(false)}
          transaction={{ ...o, target_collection: "orders" }}
          onOpened={(d) => { toast.success("Case opened"); load(); if (d?.id) navigate(`/dashboard/disputes/${d.id}`); }}
        />
      )}
      <style>{`
        .btn-primary { display: inline-flex; align-items: center; padding: 0.5rem 1rem; border-radius: 999px; font-size: 0.85rem; font-weight: 500; background: #165a71; color: white; }
        .btn-primary:hover { background: #11485a; }
        .btn-ghost { display: inline-flex; align-items: center; padding: 0.5rem 1rem; border-radius: 999px; font-size: 0.85rem; border: 1px solid #e2e8f0; color: #252A34; }
        .btn-ghost:hover { background: #f8fafc; }
      `}</style>
    </DashboardShell>
  );
}

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between text-sm">
    <span className="text-[#252A34]/70">{label}</span>
    <span className="font-medium text-[#252A34]">{value}</span>
  </div>
);

function RequirementsPanel({ o, requirements, isCustomer, onChange }) {
  const initial = Object.fromEntries((o.requirement_answers || []).map((a) => [a.requirement_id, a.text || ""]));
  const [answers, setAnswers] = useState(initial);
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/digital-orders/${o.id}/requirements`, {
        answers: requirements.map((r) => ({ requirement_id: r.id, text: answers[r.id] || "" })),
      });
      toast.success("Requirements submitted — provider can start now.");
      onChange();
    } catch (err) { toast.error(err?.response?.data?.error || "Failed"); } finally { setSaving(false); }
  };
  if (!requirements.length) return <div className="rounded-2xl border border-slate-200 bg-white p-5 text-sm text-[#252A34]/70">No requirements defined for this service.</div>;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="requirements-panel">
      <div className="font-display font-semibold text-[#071b22] mb-3">What we need from you</div>
      <div className="space-y-4">
        {requirements.map((r) => (
          <div key={r.id}>
            <div className="text-sm font-medium text-[#071b22]">{r.question} {r.required && <span className="text-red-600 text-xs ml-1">required</span>}</div>
            {r.instructions && <div className="text-xs text-[#252A34]/60 mt-0.5">{r.instructions}</div>}
            <textarea
              rows={3}
              disabled={!isCustomer || !!o.requirements_submitted_at}
              value={answers[r.id] || ""}
              onChange={(e) => setAnswers({ ...answers, [r.id]: e.target.value })}
              className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
              data-testid={`requirement-${r.id}`}
            />
          </div>
        ))}
      </div>
      {isCustomer && !o.requirements_submitted_at && (
        <button className="mt-4 rounded-full bg-[#165a71] text-white px-5 py-2.5 text-sm font-medium disabled:opacity-60" disabled={saving} onClick={submit} data-testid="requirements-submit">
          {saving ? "Submitting…" : "Submit requirements"}
        </button>
      )}
      {o.requirements_submitted_at && (
        <div className="mt-3 text-xs text-emerald-700">Submitted {new Date(o.requirements_submitted_at).toLocaleString()}.</div>
      )}
    </div>
  );
}

function DeliveriesPanel({ o, isProvider, isCustomer, onChange }) {
  const [note, setNote] = useState("");
  const [kind, setKind] = useState("final");
  const [revInstructions, setRevInstructions] = useState("");
  const submit = async () => {
    if (!note) return toast.error("Add a note to describe this delivery");
    try {
      await api.post(`/digital-orders/${o.id}/deliveries`, { note, files: [], kind });
      toast.success("Delivery sent");
      setNote("");
      onChange();
    } catch (err) { toast.error(err?.response?.data?.error || "Failed"); }
  };
  const requestRevision = async () => {
    if (!revInstructions) return toast.error("Describe the revision needed");
    try {
      await api.post(`/digital-orders/${o.id}/revision`, { instructions: revInstructions, files: [] });
      toast.success("Revision requested");
      setRevInstructions("");
      onChange();
    } catch (err) { toast.error(err?.response?.data?.error || "Failed"); }
  };
  return (
    <div className="space-y-4">
      {(o.deliveries || []).length > 0 && (
        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <div className="font-display font-semibold text-[#071b22] mb-3">Deliveries</div>
          <div className="space-y-3">
            {(o.deliveries || []).map((d) => (
              <div key={d.id} className="rounded-xl border border-slate-200 p-3 text-sm" data-testid={`delivery-${d.id}`}>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs uppercase tracking-wider text-[#165a71] font-bold">{d.kind}</span>
                  <span className="text-xs text-[#252A34]/60">{d.delivered_at ? new Date(d.delivered_at).toLocaleString() : ""}</span>
                </div>
                <div className="text-[#252A34]">{d.note}</div>
                {(d.files || []).length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {d.files.map((f, i) => <a key={i} href={f.url} className="text-xs text-[#165a71] hover:underline">{f.name}</a>)}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      {isProvider && ["in_progress", "requirements_needed", "revision_requested", "revised"].includes(o.status) && (
        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <div className="font-display font-semibold text-[#071b22] mb-3">Send a delivery</div>
          <div className="space-y-2">
            <select value={kind} onChange={(e) => setKind(e.target.value)} className="rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid="delivery-kind">
              <option value="draft">Draft</option>
              <option value="final">Final</option>
              <option value="revised">Revised</option>
            </select>
            <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" placeholder="Describe what you're delivering…" data-testid="delivery-note" />
            <button className="rounded-full bg-[#165a71] text-white px-5 py-2 text-sm font-medium" onClick={submit} data-testid="delivery-submit"><Send className="h-4 w-4 inline mr-1" /> Send delivery</button>
          </div>
        </div>
      )}
      {isCustomer && o.status === "delivered" && (
        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <div className="font-display font-semibold text-[#071b22] mb-3">Request a revision</div>
          <textarea rows={3} value={revInstructions} onChange={(e) => setRevInstructions(e.target.value)} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" placeholder="What needs to change?" data-testid="revision-instructions" />
          <div className="mt-2 flex items-center gap-2">
            <button className="rounded-full border border-slate-200 px-4 py-2 text-sm font-medium hover:bg-slate-50" onClick={requestRevision} data-testid="revision-submit">
              <RefreshCw className="h-4 w-4 inline mr-1" /> Request revision
            </button>
            <span className="text-xs text-[#252A34]/60">{o.revisions_used || 0}/{o.revision_allowance || "∞"} used</span>
          </div>
        </div>
      )}
    </div>
  );
}

function MilestonesPanel({ o, isProvider, isCustomer, onChange }) {
  const act = async (milestone_id, action, note = "") => {
    try {
      await api.post(`/digital-orders/${o.id}/milestones/action`, { milestone_id, action, note });
      toast.success(`Milestone ${action.replace(/_/g, " ")}`);
      onChange();
    } catch (err) { toast.error(err?.response?.data?.error || "Failed"); }
  };
  return (
    <div className="space-y-3" data-testid="milestones-panel">
      {(o.milestones || []).map((m, idx) => (
        <div key={m.id} className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`milestone-${m.id}`}>
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="font-display font-semibold text-[#071b22]">{idx + 1}. {m.name}</div>
              {m.description && <div className="text-sm text-[#252A34]/70 mt-0.5">{m.description}</div>}
            </div>
            <StatusPill status={m.status} />
          </div>
          <div className="text-sm text-[#252A34] flex items-center gap-3">
            <span>${(m.amount || 0).toFixed(2)}</span>
            {m.due_date && <span className="text-xs text-[#252A34]/60">Due {new Date(m.due_date).toLocaleDateString()}</span>}
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {isProvider && m.status === "active" && (
              <button className="rounded-full bg-[#165a71] text-white px-4 py-2 text-xs font-medium" onClick={() => act(m.id, "submit")}>Submit for review</button>
            )}
            {isCustomer && m.status === "submitted" && (
              <>
                <button className="rounded-full bg-emerald-600 text-white px-4 py-2 text-xs font-medium" onClick={() => act(m.id, "approve")} data-testid={`milestone-approve-${m.id}`}>Approve</button>
                <button className="rounded-full border border-slate-200 px-4 py-2 text-xs font-medium" onClick={() => act(m.id, "request_revision", window.prompt("Revision note?") || "")}>Request revision</button>
              </>
            )}
            {isCustomer && m.status === "approved" && (
              <button
                className="rounded-full bg-[#165a71] text-white px-4 py-2 text-xs font-medium inline-flex items-center gap-1"
                onClick={async () => {
                  try {
                    const { data } = await api.post("/payments/checkout", {
                      purpose: "milestone", target_id: o.id, milestone_id: m.id,
                      origin_url: window.location.origin,
                    });
                    window.location.href = data.checkout_url;
                  } catch (err) { toast.error(err?.response?.data?.error || "Cannot start payment"); }
                }}
                data-testid={`milestone-pay-${m.id}`}
              >Pay ${(m.amount || 0).toFixed(2)}</button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
